import { useEffect, useState } from 'react'

import { loadGoogleMaps, isGoogleMapsReady } from 'utils/loadGoogleMaps'

/**
 * Pulls in the Google Maps SDK when a component that needs it mounts.
 *
 * Returns `ready` once `google.maps.places` is usable. Components should render
 * their input immediately and only attach autocomplete behaviour once `ready`
 * is true, so a slow or blocked script degrades to plain text entry rather than
 * an empty field.
 *
 * See `utils/loadGoogleMaps` for why the SDK is no longer loaded on every page
 * (DT-012).
 */
export const useGoogleMaps = (): { ready: boolean } => {
	const [ready, setReady] = useState(isGoogleMapsReady())

	useEffect(() => {
		if (ready) return

		let cancelled = false

		loadGoogleMaps().then(google => {
			// Guard against setting state after unmount — these screens are
			// frequently opened in a sheet or modal that can close mid-load.
			if (!cancelled && google) setReady(true)
		})

		return () => {
			cancelled = true
		}
	}, [ready])

	return { ready }
}

export default useGoogleMaps
